import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { displayCents, type MoneyMode } from '../../lib/money';
import { daysOverdue } from '../../../shared/attention';
import { addDays, CalendarPanel, formatInvoiceNumber, formatMoney } from '../../components/primitives';
import { AlertIcon, ArrowIcon, CalendarIcon, MInvoiceIcon, MoreIcon, SnoozeIcon } from '../../components/icons';
import { DateSheet } from '../../components/DateSheet';
import { PageMenu } from '../../components/PageMenu';
import { SwipeRow } from '../../components/SwipeRow';
import { useIsMobile } from '../../lib/useIsMobile';
import { reportError } from '../../lib/errorEvents';
import type { OverdueInvoice } from './types';

/** Overdue invoice attention row with the quiet snooze affordance (Henry's
    walkthrough note): +3d / +1w / pick date hide it from this feed until
    the chosen date — the invoice record itself is untouched. */
export function InvoiceAttentionRow({
  inv,
  today,
  mode,
  onChanged,
}: {
  inv: OverdueInvoice;
  today: string;
  mode: MoneyMode;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [pickOpen, setPickOpen] = useState(false);
  const pickRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  // Mobile: the snooze chips leave the row for a swipe action and a row
  // sheet — nothing is gesture-only (decision 6).
  const [rowSheet, setRowSheet] = useState<null | 'more' | 'date'>(null);
  const days = inv.due_date ? daysOverdue(inv.due_date, today) : null;

  const snooze = async (until: string) => {
    await api(`/api/admin/finance/invoices/${inv.id}`, {
      method: 'PATCH',
      json: { attention_snoozed_until: until },
    }).catch((error) => reportError(error, 'Could not snooze the invoice.'));
    onChanged();
  };

  const number = formatInvoiceNumber(inv.invoice_number);
  const money = formatMoney(displayCents(inv.amount_cents, inv.gst_cents, mode));
  const overdueText = days !== null ? `${days}d overdue` : 'Overdue';

  if (isMobile) {
    return (
      <>
        <SwipeRow
          leftActions={[
            {
              label: '+3d',
              icon: <SnoozeIcon />,
              tone: 'gold',
              onSelect: () => void snooze(addDays(today, 3)),
            },
            { label: 'More', icon: <MoreIcon />, tone: 'grey', onSelect: () => setRowSheet('more') },
          ]}
        >
          <div className="row mrow-att" onClick={() => navigate(`/finance/invoices/${inv.id}`)}>
            <span className="ico d">
              <MInvoiceIcon />
            </span>
            <div className="main">
              <div className="ttl">
                Invoice {number} — {inv.client_name}
              </div>
              <div className="sub">
                <span className="d">{overdueText}</span>
              </div>
            </div>
            <span className="tail money">{money}</span>
            <button
              type="button"
              className="rmore"
              aria-label="More actions"
              onClick={(e) => {
                e.stopPropagation();
                setRowSheet('more');
              }}
            >
              <MoreIcon />
            </button>
          </div>
        </SwipeRow>

        <PageMenu
          open={rowSheet === 'more'}
          onClose={() => setRowSheet(null)}
          items={[
            {
              key: '3d',
              icon: <SnoozeIcon />,
              label: '+3 days',
              onSelect: () => void snooze(addDays(today, 3)),
            },
            {
              key: '1w',
              icon: <SnoozeIcon />,
              label: '+1 week',
              onSelect: () => void snooze(addDays(today, 7)),
            },
            {
              key: 'pick',
              icon: <CalendarIcon />,
              label: 'Snooze…',
              onSelect: () => setRowSheet('date'),
            },
            {
              key: 'open',
              icon: <ArrowIcon />,
              label: 'Open invoice',
              onSelect: () => navigate(`/finance/invoices/${inv.id}`),
            },
          ]}
        />

        <DateSheet
          open={rowSheet === 'date'}
          title={`Snooze invoice ${number}`}
          context={`${inv.client_name} · ${money} · ${overdueText}`}
          value={null}
          chips={[
            { label: '+3 days', date: addDays(today, 3) },
            { label: '+1 week', date: addDays(today, 7) },
          ]}
          verb="Hide until"
          onPick={(iso) => {
            setRowSheet(null);
            if (iso) void snooze(iso);
          }}
          onClose={() => setRowSheet(null)}
        />
      </>
    );
  }

  return (
    <div className="row" onClick={() => navigate(`/finance/invoices/${inv.id}`)} style={{ cursor: 'pointer' }}>
      <span className="row-icon ic-danger">
        <AlertIcon />
      </span>
      <span className="row-title" title={`Invoice ${formatInvoiceNumber(inv.invoice_number)} — ${inv.client_name}`}>
        Invoice {formatInvoiceNumber(inv.invoice_number)} — {inv.client_name}
      </span>
      <span className="row-spacer" />
      <span className="row-snooze" onClick={(e) => e.stopPropagation()}>
        <button className="chip chip-quick" title="Hide for 3 days" onClick={() => snooze(addDays(today, 3))}>
          +3d
        </button>
        <button className="chip chip-quick" title="Hide for a week" onClick={() => snooze(addDays(today, 7))}>
          +1w
        </button>
        <button className="chip" ref={pickRef} onClick={() => setPickOpen((o) => !o)}>
          Snooze…
        </button>
        {pickOpen && (
          <CalendarPanel
            value={null}
            anchorRef={pickRef}
            onPick={(iso) => iso && snooze(iso)}
            onClose={() => setPickOpen(false)}
          />
        )}
      </span>
      <span className="row-meta danger">{days !== null ? `${days}d overdue` : 'Overdue'}</span>
      <span className="row-meta gold">{formatMoney(displayCents(inv.amount_cents, inv.gst_cents, mode))}</span>
    </div>
  );
}
